import { useState, useEffect, useMemo, useRef } from 'react'
import { useAction } from '@/hooks/useAction'
import { useLanguage } from '@/hooks/useLanguage'
import { DREAMITY_LOCAL_SNAPSHOT_REPLACED_EVENT } from '@/services/dreamityLocalSnapshot'
import { DreamityActionResDto } from '@/store/types/calendar.types'
import Modal from '@/components/common/Modal/Modal'
import {
  IconChevronLeft,
  IconChevronRight,
  IconChevronDown,
  IconClock,
  IconCheck,
  IconDream,
  IconTarget,
  IconClipboard
} from '@/components/icons/Icons'

import './Calendar.css'

const WEEKDAYS_KO = ['일', '월', '화', '수', '목', '금', '토']
const WEEKDAYS_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function formatYmd(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function formatYyyymmdd(date: Date): string {
  return formatYmd(date).replace(/-/g, '')
}

function Calendar() {
  const { language } = useLanguage()
  const { actions, fetchActions } = useAction()

  const [currentDate, setCurrentDate] = useState(() => new Date())
  const todayStr = formatYmd(new Date())
  const [selectedDate, setSelectedDate] = useState<string | null>(todayStr)
  const [detailModalOpen, setDetailModalOpen] = useState(false)
  const [pickerModalOpen, setPickerModalOpen] = useState(false)
  const hasFetchedRef = useRef<string | null>(null)

  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()

  const { startYmd, endYmd, startCompact, endCompact } = useMemo(() => {
    const start = new Date(year, month, 1)
    const end = new Date(year, month + 1, 0)
    return {
      startYmd: formatYmd(start),
      endYmd: formatYmd(end),
      startCompact: formatYyyymmdd(start),
      endCompact: formatYyyymmdd(end),
    }
  }, [year, month])

  useEffect(() => {
    const key = `${startCompact}-${endCompact}`
    if (hasFetchedRef.current === key) return
    hasFetchedRef.current = key
    const load = async () => {
      try {
        await fetchActions(startYmd, endYmd)
      } catch {
        hasFetchedRef.current = null
      }
    }
    void load()
  }, [startYmd, endYmd, startCompact, endCompact, fetchActions])

  useEffect(() => {
    const onSnapshotReplaced = () => {
      hasFetchedRef.current = null
      void fetchActions(startYmd, endYmd)
    }
    window.addEventListener(DREAMITY_LOCAL_SNAPSHOT_REPLACED_EVENT, onSnapshotReplaced)
    return () => {
      window.removeEventListener(DREAMITY_LOCAL_SNAPSHOT_REPLACED_EVENT, onSnapshotReplaced)
    }
  }, [startYmd, endYmd, fetchActions])

  const actionsByDate = useMemo(() => {
    const map = new Map<string, DreamityActionResDto[]>()
    for (const a of actions) {
      if (a.isDeleted) continue
      const list = map.get(a.actionDate) ?? []
      list.push(a)
      map.set(a.actionDate, list)
    }
    return map
  }, [actions])

  const firstDayWeekday = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const cells: ({ day: number } | null)[] = []
  for (let i = 0; i < firstDayWeekday; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push({ day: d })
  while (cells.length % 7 !== 0) cells.push(null)

  const weekLabels = language === 'ko' ? WEEKDAYS_KO : WEEKDAYS_EN

  const selectedActions =
    selectedDate != null ? actionsByDate.get(selectedDate) ?? [] : []

  const handlePrevMonth = () => setCurrentDate(new Date(year, month - 1, 1))
  const handleNextMonth = () => setCurrentDate(new Date(year, month + 1, 1))
  const handleGoToday = () => {
    const today = new Date()
    setCurrentDate(today)
    setSelectedDate(formatYmd(today))
  }


  const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]

  return (
    <div className="calendar">
      <div className="calendar__header">
        <div className="calendar__header-left">
          <button 
            className="calendar__picker-trigger" 
            onClick={() => setPickerModalOpen(true)}
          >
            <span className="calendar__picker-text">
              {year}{language === 'ko' ? '년 ' : ' '}{month + 1}{language === 'ko' ? '월' : ''}
            </span>
            <IconChevronDown size={16} />
          </button>
          <button className="calendar__today-btn" onClick={handleGoToday} aria-label={language === 'ko' ? '오늘' : 'Today'}>
            <IconClock size={18} />
          </button>
        </div>

        <div className="calendar__header-right">
          <div className="calendar__nav-group">
            <button className="calendar__nav-icon-btn" onClick={handlePrevMonth} aria-label="Previous month">
              <IconChevronLeft size={20} />
            </button>
            <button className="calendar__nav-icon-btn" onClick={handleNextMonth} aria-label="Next month">
              <IconChevronRight size={20} />
            </button>
          </div>
        </div>
      </div>

      <div className="calendar__card">
        <div className="calendar__grid">
          {weekLabels.map((w) => (
            <div key={w} className="calendar__day-header">
              {w}
            </div>
          ))}
          {cells.map((cell, idx) => {
            if (!cell) return <div key={`e-${idx}`} className="calendar__day empty" />
            
            const dateStr = formatYmd(new Date(year, month, cell.day))
            const dayActions = actionsByDate.get(dateStr) ?? []
            const isToday = dateStr === todayStr
            const isSelected = dateStr === selectedDate
            
            return (
              <button
                key={dateStr}
                type="button"
                className={`calendar__day${isToday ? ' today' : ''}${isSelected ? ' selected' : ''}`}
                onClick={() => {
                  setSelectedDate(dateStr)
                  setDetailModalOpen(true)
                }}
              >
                <span className="calendar__day-number">{cell.day}</span>
                {dayActions.length > 0 && (
                  <div className="calendar__dots">
                    {dayActions.slice(0, 3).map((a, i) => (
                      <div key={i} className={`calendar__dot ${a.isCompleted ? 'completed' : ''}`} />
                    ))}
                    {dayActions.length > 3 && <div className="calendar__dot" style={{ opacity: 0.5 }} />}
                  </div>
                )}
              </button>
            )
          })}
        </div>
      </div>

      <div className="calendar__today-section">
        <h3 className="calendar__today-title">
          {language === 'ko' ? '오늘의 할 일' : "Today's Tasks"}
          <span className="calendar__today-count">{actionsByDate.get(todayStr)?.length || 0}</span>
        </h3>
        <div className="calendar__today-list">
          {!actionsByDate.get(todayStr) || actionsByDate.get(todayStr)?.length === 0 ? (
            <div className="calendar__today-empty">
              {language === 'ko' ? '오늘 할 일이 없습니다.' : 'No tasks for today.'}
            </div>
          ) : (
            actionsByDate.get(todayStr)?.map((a) => (
              <div 
                key={String(a.actionId)} 
                className={`calendar__today-item ${a.isCompleted ? 'completed' : ''}`}
                onClick={() => {
                  setSelectedDate(todayStr)
                  setDetailModalOpen(true)
                }}
              >
                <div className="calendar__today-item-info">
                  <span className="calendar__today-item-name">{a.actionName}</span>
                  {a.actionTime && <span className="calendar__today-item-time">{a.actionTime.slice(0, 5)}</span>}
                </div>
                {a.isCompleted ? (
                  <div className="calendar__today-item-status completed">
                    <IconCheck size={14} />
                  </div>
                ) : (
                  <div className="calendar__today-item-status pending" />
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <Modal
        isOpen={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        title={selectedDate ?? ''}
        size="md"
      >
        <div className="calendar__selected-date-list">
          {selectedActions.length === 0 ? (
            <p className="calendar__loading">
              {language === 'ko' ? '등록된 일정이 없습니다.' : 'No actions.'}
            </p>
          ) : (
            selectedActions.map((a) => (
              <div key={String(a.actionId)} className={`calendar__action-card ${a.isCompleted ? 'completed' : ''}`}>
                <div className="calendar__action-header">
                  <h4 className="calendar__action-title">{a.actionName}</h4>
                </div>
                <div className="calendar__action-tags">
                  {a.dreamName && <span className="calendar__tag"><IconDream size={10} /> {a.dreamName}</span>}
                  {a.goalName && <span className="calendar__tag"><IconTarget size={10} /> {a.goalName}</span>}
                  {a.planName && <span className="calendar__tag"><IconClipboard size={10} /> {a.planName}</span>}
                  {a.isCompleted && <span className="calendar__tag" style={{ background: '#ecfdf5', color: '#059669' }}><IconCheck size={10} /> 완료됨</span>}
                </div>
                {a.actionDesc && <p className="calendar__action-desc">{a.actionDesc}</p>}
              </div>
            ))
          )}
        </div>
      </Modal>

      {/* 년/월 선택 모달 */}
      <Modal
        isOpen={pickerModalOpen}
        onClose={() => setPickerModalOpen(false)}
        title={language === 'ko' ? '이동할 년/월 선택' : 'Select Year/Month'}
        size="sm"
      >
        <div className="calendar__picker-modal">
          <div className="calendar__picker-year-row">
            <button 
              className="calendar__picker-year-nav" 
              onClick={() => setCurrentDate(new Date(year - 1, month, 1))}
            >
              <IconChevronLeft size={18} />
            </button>
            <span className="calendar__picker-year-display">{year}{language === 'ko' ? '년' : ''}</span>
            <button 
              className="calendar__picker-year-nav"
              onClick={() => setCurrentDate(new Date(year + 1, month, 1))}
            >
              <IconChevronRight size={18} />
            </button>
          </div>
          <div className="calendar__picker-month-grid">
            {months.map((m, idx) => (
              <button
                key={m}
                className={`calendar__picker-month-item${month === idx ? ' active' : ''}`}
                onClick={() => {
                  setCurrentDate(new Date(year, idx, 1))
                  setPickerModalOpen(false)
                }}
              >
                {m}{language === 'ko' ? '월' : ''}
              </button>
            ))}
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default Calendar
